// Shared theme color-picker pieces, extracted from ThemeSettings.js (the
// self-service tenant theming page) so TenantManagement.js's admin theme
// editor doesn't duplicate this UI — same ColorField/SectionCard/field lists,
// just reused from a common module instead of copy-pasted.
import React, { useState, useRef, useEffect } from "react";
import { HexColorPicker } from "react-colorful";

export const BRAND_COLOR_FIELDS = [
  { key: "primaryColor", label: "Primary Color" },
  { key: "secondaryColor", label: "Secondary Color" },
  { key: "tertiaryColor", label: "Tertiary Color" },
  { key: "sidebarColor", label: "Sidebar Color" },
  { key: "userbarColor", label: "Background Color" },
  { key: "surfaceColor", label: "Surface Color" },
];

export const FONT_COLOR_FIELDS = [
  { key: "primaryFontColor", label: "Primary Font Color" },
  { key: "secondaryFontColor", label: "Secondary Font Color" },
];

export const ColorField = ({ label, value, onChange, align = "left", vAlign = "top" }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div
      className="flex flex-col gap-2 relative rounded-xl border border-canvas-tersery/40 p-3 hover:border-canvas-primary/50 hover:shadow-md transition-all duration-200"
      ref={containerRef}
    >
      <h6 className="text-canvas-directoryText">{label}</h6>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={`Pick ${label}`}
        className="flex items-center gap-2 group"
      >
        <span
          className="w-9 h-9 rounded-full border-2 border-canvas-white shrink-0 shadow-[0_0_0_1.5px] shadow-canvas-tersery/50 group-hover:scale-110 transition-transform duration-200"
          style={{ backgroundColor: value }}
        />
        <span className="font-outfit text-xs font-semibold text-canvas-black uppercase tracking-wide">
          {value}
        </span>
      </button>
      {open && (
        <div
          className={`absolute z-50 p-3 bg-white rounded-xl shadow-2xl border border-canvas-tersery/50 ${
            align === "right" ? "right-0" : "left-0"
          } ${vAlign === "top" ? "bottom-full mb-2" : "top-full mt-2"}`}
        >
          <HexColorPicker color={value} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

export const SectionCard = ({ title, description, children }) => (
  <div className="flex flex-col gap-3 px-5 py-3">
    <div className="flex items-center gap-3">
      <span className="w-1 h-6 rounded-full bg-canvas-primary shrink-0" />
      <div className="flex flex-col gap-0.5">
        <h4 className="text-canvas-primary">{title}</h4>
        {description && <h6 className="text-canvas-directoryText font-normal">{description}</h6>}
      </div>
    </div>
    {children}
  </div>
);
